import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectProps } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import MenuIcon from "@mui/icons-material/Menu";
import { alpha, styled } from "@mui/material/styles";
import { useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { navigationItems } from "../router/navigation";
import { useAppContext } from "../hooks/useAppContext";
import type { AppLanguage } from "../i18n";
import { supportedLanguages } from "../i18n";

const LogoLink = styled(NavLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  paddingInline: theme.spacing(0.5),
  textDecoration: "none",
}));

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  width: 42,
  height: 42,
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.04),
  [theme.breakpoints.down("sm")]: {
    width: 34,
    height: 34,
  },
}));

const NavigationWrapper = styled("div")(({ theme }) => ({
  position: "fixed",
  top: theme.spacing(2),
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: theme.zIndex.appBar,
  width: "max-content",
  maxWidth: "calc(100vw - 24px)",
  [theme.breakpoints.down("sm")]: {
    width: "calc(100vw - 24px)",
  },
}));

const NavShell = styled("nav")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  width: "max-content",
  maxWidth: "100%",
  padding: theme.spacing(1),
  borderRadius: 999,
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.72),
  backdropFilter: "blur(18px)",
  boxShadow: "0 16px 40px rgba(0, 0, 0, 0.28)",
  overflowX: "auto",
  overflowY: "hidden",
  whiteSpace: "nowrap",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "space-between",
    padding: theme.spacing(0.875, 1),
  },
}));

const DesktopNavItems = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const MobileActiveLabel = styled(Typography)(({ theme }) => ({
  display: "none",
  color: theme.palette.text.primary,
  flex: 1,
  minWidth: 0,
  paddingInline: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: "none",
  borderRadius: 999,
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  [theme.breakpoints.down("sm")]: {
    display: "inline-flex",
  },
}));

const NavActions = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  marginLeft: "auto",
}));

const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 999,
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
}));

const LanguageSwitcher = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const LanguageSelect = styled(Select)(({ theme }) => ({
  minWidth: 58,
  height: 40,
  borderRadius: 999,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.common.white, 0.08),
  },
  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.28),
    },
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "unset",
    padding: "8px 30px 8px 12px",
  },
  "& .MuiSelect-icon": {
    right: 10,
  },
}));

const MobileLanguageRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(0.25),
}));

const FlagOption = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
  lineHeight: 1,
});

const languageFlags: Record<AppLanguage, string> = {
  it: "🇮🇹",
  en: "🇬🇧",
  fr: "🇫🇷",
};

const MobileMenuPanel = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: "grid",
  gap: theme.spacing(0.5),
  width: "100%",
  padding: theme.spacing(1),
  borderRadius: 24,
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.82),
  backdropFilter: "blur(20px)",
  boxShadow: "0 18px 48px rgba(0, 0, 0, 0.32)",
}));

const MobileNavItemLink = styled(NavLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  minHeight: 44,
  padding: theme.spacing(1.25, 1.5),
  borderRadius: 16,
  color: theme.palette.text.secondary,
  textDecoration: "none",
  transition: "background-color 180ms ease, color 180ms ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
  },
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    color: theme.palette.text.primary,
  },
}));

const NavItemLink = styled(NavLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 40,
  padding: theme.spacing(1, 2),
  borderRadius: 999,
  color: theme.palette.text.secondary,
  textDecoration: "none",
  transition:
    "background-color 180ms ease, color 180ms ease, transform 180ms ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
  },
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.875, 1.5),
  },
}));

const NavigationBar = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const {
    state: { themeMode },
    toggleTheme,
  } = useAppContext();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const activeNavigationItem =
    navigationItems.find((item) =>
      item.path === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(item.path),
    ) ?? navigationItems[0];

  const nextThemeMode = themeMode === "dark" ? "light" : "dark";
  const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? "it").slice(
    0,
    2,
  ) as AppLanguage;
  const isMobileMenuOpen = isMobile && isMenuOpen;

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLanguageChange: NonNullable<SelectProps["onChange"]> = (event) => {
    handleCloseMenu();
    void i18n.changeLanguage(event.target.value as AppLanguage);
  };

  return (
    <NavigationWrapper>
      <NavShell aria-label={t("navigation.ariaLabel")}>
        <LogoLink
          to="/"
          aria-label={t("app.homeLinkAriaLabel")}
          onClick={handleCloseMenu}
        >
          <LogoAvatar src="/images/drakkar.png" alt={t("app.logoAlt")} />
        </LogoLink>
        <Typography fontWeight={500} component="span" sx={{ mr: 2 }}>
          {t("app.ownerName")}
        </Typography>

        <DesktopNavItems>
          {navigationItems.map((item) => (
            <NavItemLink key={item.path} to={item.path} end={item.path === "/"}>
              <Typography fontWeight={600} component="span">
                {t(item.labelKey)}
              </Typography>
            </NavItemLink>
          ))}
        </DesktopNavItems>

        <MobileActiveLabel fontWeight={600}>
          {t(activeNavigationItem.labelKey)}
        </MobileActiveLabel>

        <NavActions>
          <Tooltip title={''}>
            <LanguageSwitcher>
              <LanguageSelect
                value={currentLanguage}
                onChange={handleLanguageChange}
                aria-label={t("navigation.switchLanguage")}
                size="small"
                renderValue={(language) => (
                  <FlagOption>{languageFlags[language as AppLanguage]}</FlagOption>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      borderRadius: 3,
                      minWidth: 72,
                    },
                  },
                }}
              >
                {supportedLanguages.map((language) => (
                  <MenuItem
                    key={language}
                    value={language}
                    aria-label={`${t("navigation.switchLanguage")}: ${language.toUpperCase()}`}
                    sx={{ justifyContent: "center", minHeight: 44 }}
                  >
                    <FlagOption>{languageFlags[language]}</FlagOption>
                  </MenuItem>
                ))}
              </LanguageSelect>
            </LanguageSwitcher>
          </Tooltip>

          <ThemeToggleButton
            aria-label={
              nextThemeMode === "light"
                ? t("navigation.switchThemeToLight")
                : t("navigation.switchThemeToDark")
            }
            onClick={toggleTheme}
          >
            {themeMode === "dark" ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
          </ThemeToggleButton>

          <MobileMenuButton
            aria-label={t("navigation.openMenu")}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMenuOpen((current) => !current)}
          >
            <MenuIcon />
          </MobileMenuButton>
        </NavActions>
      </NavShell>

      {isMobileMenuOpen ? (
        <MobileMenuPanel>
          <MobileLanguageRow>
            <LanguageSelect
              value={currentLanguage}
              onChange={handleLanguageChange}
              aria-label={t("navigation.switchLanguage")}
              size="small"
              renderValue={(language) => (
                <FlagOption>{languageFlags[language as AppLanguage]}</FlagOption>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    borderRadius: 3,
                    minWidth: 72,
                  },
                },
              }}
            >
              {supportedLanguages.map((language) => (
                <MenuItem
                  key={language}
                  value={language}
                  aria-label={`${t("navigation.switchLanguage")}: ${language.toUpperCase()}`}
                  sx={{ justifyContent: "center", minHeight: 44 }}
                >
                  <FlagOption>{languageFlags[language]}</FlagOption>
                </MenuItem>
              ))}
            </LanguageSelect>
          </MobileLanguageRow>

          {navigationItems.map((item) => (
            <MobileNavItemLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={handleCloseMenu}
            >
              <Typography fontWeight={600} component="span">
                {t(item.labelKey)}
              </Typography>
            </MobileNavItemLink>
          ))}
        </MobileMenuPanel>
      ) : null}
    </NavigationWrapper>
  );
};

export default NavigationBar;
